// File: src/components/Admin/BundleManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tab,
  Tabs,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  LocalOffer as OfferIcon,
  Inventory as BundleIcon,
  TrendingUp as TrendingUpIcon,
  Save as SaveIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Bundle {
  id: number;
  bundle_name: string;
  bundle_code: string;
  description: string;
  is_active: boolean;
  requires_voip: boolean;
  requires_internet: boolean;
  min_services: number;
  active_discounts: number;
  active_subscriptions: number;
  discount_rules: DiscountRule[];
}

interface DiscountRule {
  id?: number;
  bundle_id?: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_term_months: number;
  description: string;
  is_active: boolean;
}

interface BundlePricing {
  bundle_name: string;
  voip_price: number;
  voip_packages?: Array<{id: string; name: string; price: number}>;
  internet_price: number;
  internet_services?: Array<{id: string; name: string; price: number}>;
  addon_price: number;
  subtotal: number;
  discount_amount: number;
  discount_percentage: number;
  total_monthly: number;
  setup_fee: number;
  savings: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bundle-tabpanel-${index}`}
      aria-labelledby={`bundle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const BundleManagement: React.FC = () => {
  const { member } = useAuth();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [retailServices, setRetailServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog states
  const [bundleDialog, setBundleDialog] = useState(false);
  const [pricingDialog, setPricingDialog] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [isNew, setIsNew] = useState(false);
  
  // Pricing calculator state
  const [pricingBundle, setPricingBundle] = useState<Bundle | null>(null);
  const [pricingParams, setPricingParams] = useState({
    bundle_id: 0,
    voip_package_id: '',
    internet_service_ids: [] as string[],
    addon_ids: [] as string[],
    contract_months: 0,
  });
  const [calculatedPrice, setCalculatedPrice] = useState<BundlePricing | null>(null);
  const [voipPackages, setVoipPackages] = useState<any[]>([]);
  
  // Form state for new/edit bundle
  const [formData, setFormData] = useState({
    bundle_name: '',
    bundle_code: '',
    description: '',
    requires_voip: true,
    requires_internet: true,
    min_services: 2,
    is_active: true,
    discount_rules: [] as DiscountRule[],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load bundles and services first
      const [bundlesRes, servicesRes] = await Promise.all([
        api.get('/endpoints/bundles'),
        api.get('/endpoints/internetvendors/retail')
      ]);
      
      setBundles(bundlesRes.data.data || []);
      setRetailServices(servicesRes.data.data || []);
      
      // Load packages with proper response handling
      try {
        const packagesRes = await api.get('/endpoints/packages_list');
        console.log('Packages response:', packagesRes.data);
        
        // Check for packages in different possible locations
        let packages = [];
        if (packagesRes.data && packagesRes.data.success) {
          if (packagesRes.data.data && packagesRes.data.data.packages) {
            // Format: { success: true, data: { packages: [...] } }
            packages = packagesRes.data.data.packages;
          } else if (Array.isArray(packagesRes.data.data)) {
            // Format: { success: true, data: [...] }
            packages = packagesRes.data.data;
          }
        }
        
        console.log('Setting packages:', packages);
        setVoipPackages(packages);
      } catch (pkgErr: any) {
        console.error('Failed to load packages:', pkgErr);
        console.error('Package error response:', pkgErr.response);
        setVoipPackages([]);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Load data error:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBundles = loadData;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddBundle = () => {
    setSelectedBundle(null);
    setFormData({
      bundle_name: '',
      bundle_code: '',
      description: '',
      requires_voip: true,
      requires_internet: true,
      min_services: 2,
      is_active: true,
      discount_rules: [
        {
          discount_type: 'percentage',
          discount_value: 10,
          min_term_months: 0,
          description: 'Standard bundle discount',
          is_active: true,
        },
      ],
    });
    setIsNew(true);
    setBundleDialog(true);
  };

  const handleEditBundle = (bundle: Bundle) => {
    setSelectedBundle(bundle);
    setFormData({
      bundle_name: bundle.bundle_name,
      bundle_code: bundle.bundle_code,
      description: bundle.description,
      requires_voip: bundle.requires_voip,
      requires_internet: bundle.requires_internet,
      min_services: bundle.min_services,
      is_active: bundle.is_active,
      discount_rules: bundle.discount_rules.length > 0 ? bundle.discount_rules : [{
        discount_type: 'percentage',
        discount_value: 10,
        min_term_months: 0,
        description: 'Standard bundle discount',
        is_active: true,
      }],
    });
    setIsNew(false);
    setBundleDialog(true);
  };

  const handleSaveBundle = async () => {
    try {
      if (isNew) {
        await api.post('/endpoints/bundles', formData);
        setSuccess('Bundle created successfully');
      } else {
        await api.put(`/endpoints/bundles/${selectedBundle?.id}`, formData);
        setSuccess('Bundle updated successfully');
      }
      setBundleDialog(false);
      fetchBundles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save bundle');
    }
  };

  const handleDeleteBundle = async (bundleId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this bundle?')) return;
    
    try {
      await api.delete(`/endpoints/bundles/${bundleId}`);
      setSuccess('Bundle deactivated successfully');
      fetchBundles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bundle');
    }
  };

  const handleOpenPricing = (bundle: Bundle) => {
    setPricingBundle(bundle);
    setPricingParams({
      bundle_id: bundle.id,
      voip_package_id: '',
      internet_service_ids: [],
      addon_ids: [],
      contract_months: 0,
    });
    setCalculatedPrice(null);
    setPricingDialog(true);
  };

  const handleCalculatePrice = async () => {
    try {
      const response = await api.post('/endpoints/bundles/calculate-price', pricingParams);
      setCalculatedPrice(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to calculate price');
    }
  };

  const handleAddDiscountRule = () => {
    setFormData({
      ...formData,
      discount_rules: [...formData.discount_rules, {
        discount_type: 'percentage',
        discount_value: 0,
        min_term_months: 0,
        description: '',
        is_active: true,
      }],
    });
  };

  const handleUpdateDiscountRule = (index: number, field: string, value: any) => {
    const updatedRules = [...formData.discount_rules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setFormData({ ...formData, discount_rules: updatedRules });
  };

  const handleRemoveDiscountRule = (index: number) => {
    const updatedRules = formData.discount_rules.filter((_, i) => i !== index);
    setFormData({ ...formData, discount_rules: updatedRules });
  };

  const BundlesTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Service Bundles</Typography>
        {member?.memberType === 'master_admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBundle}
          >
            Create Bundle
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bundle Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Requirements</TableCell>
              <TableCell align="center">Active Discounts</TableCell>
              <TableCell align="center">Active Subscriptions</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bundles.map((bundle) => (
              <TableRow key={bundle.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {bundle.bundle_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {bundle.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{bundle.bundle_code}</TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    {bundle.requires_voip && (
                      <Chip label="PBX" size="small" color="primary" />
                    )}
                    {bundle.requires_internet && (
                      <Chip label="Internet" size="small" color="secondary" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={bundle.active_discounts}
                    size="small"
                    color={bundle.active_discounts > 0 ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="center">{bundle.active_subscriptions}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={bundle.is_active ? 'Active' : 'Inactive'}
                    color={bundle.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Calculate pricing">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPricing(bundle)}
                    >
                      <CalculateIcon />
                    </IconButton>
                  </Tooltip>
                  {member?.memberType === 'master_admin' && (
                    <>
                      <Tooltip title="Edit bundle">
                        <IconButton
                          size="small"
                          onClick={() => handleEditBundle(bundle)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete bundle">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBundle(bundle.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const PricingSummaryTab = () => (
    <Box>
      <Typography variant="h6" mb={3}>Bundle Pricing Summary</Typography>
      
      <Grid container spacing={3}>
        {bundles.filter(b => b.is_active).map((bundle) => (
          <Grid item xs={12} md={4} key={bundle.id}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <BundleIcon color="primary" />
                  <Typography variant="h6">{bundle.bundle_name}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {bundle.description}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Discount Rules:
                </Typography>
                
                <List dense>
                  {bundle.discount_rules.filter(r => r.is_active).map((rule, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={rule.description}
                        secondary={
                          rule.discount_type === 'percentage'
                            ? `${rule.discount_value}% off`
                            : `$${rule.discount_value} off`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Box mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CalculateIcon />}
                    onClick={() => handleOpenPricing(bundle)}
                  >
                    Calculate Price
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bundle Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Bundles" icon={<BundleIcon />} iconPosition="start" />
            <Tab label="Pricing Summary" icon={<TrendingUpIcon />} iconPosition="start" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <BundlesTab />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PricingSummaryTab />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Bundle Dialog */}
      <Dialog open={bundleDialog} onClose={() => setBundleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isNew ? 'Create Bundle' : 'Edit Bundle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bundle Name"
                value={formData.bundle_name}
                onChange={(e) => setFormData({ ...formData, bundle_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bundle Code"
                value={formData.bundle_code}
                onChange={(e) => setFormData({ ...formData, bundle_code: e.target.value.toUpperCase() })}
                helperText="Unique identifier for the bundle"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_voip}
                    onChange={(e) => setFormData({ ...formData, requires_voip: e.target.checked })}
                  />
                }
                label="Requires PBX"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_internet}
                    onChange={(e) => setFormData({ ...formData, requires_internet: e.target.checked })}
                  />
                }
                label="Requires Internet"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">Discount Rules</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddDiscountRule}
                >
                  Add Rule
                </Button>
              </Box>
              
              {formData.discount_rules.map((rule, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={rule.discount_type}
                          onChange={(e) => handleUpdateDiscountRule(index, 'discount_type', e.target.value)}
                          label="Type"
                        >
                          <MenuItem value="percentage">Percentage</MenuItem>
                          <MenuItem value="fixed">Fixed Amount</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Value"
                        type="number"
                        value={rule.discount_value}
                        onChange={(e) => handleUpdateDiscountRule(index, 'discount_value', parseFloat(e.target.value) || 0)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {rule.discount_type === 'percentage' ? '%' : '$'}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Term"
                        type="number"
                        value={rule.min_term_months}
                        onChange={(e) => handleUpdateDiscountRule(index, 'min_term_months', parseInt(e.target.value) || 0)}
                        helperText="Months"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={rule.description}
                        onChange={(e) => handleUpdateDiscountRule(index, 'description', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveDiscountRule(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBundleDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveBundle} variant="contained">
            {isNew ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Calculator Dialog */}
      <Dialog open={pricingDialog} onClose={() => setPricingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bundle Pricing Calculator - {pricingBundle?.bundle_name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select services to calculate bundle pricing and see available discounts
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>PBX Package</InputLabel>
                <Select
                  value={pricingParams.voip_package_id}
                  onChange={(e) => setPricingParams({ ...pricingParams, voip_package_id: e.target.value as string })}
                  label="PBX Package"
                >
                  <MenuItem value="">None</MenuItem>
                  {voipPackages && Array.isArray(voipPackages) && voipPackages.map((pkg) => (
                    <MenuItem key={pkg.PackageID} value={pkg.PackageID.toString()}>
                      {pkg.PackageName} - ${pkg.MonthlyPrice}/mo
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Internet Services</InputLabel>
                <Select
                  multiple
                  value={pricingParams.internet_service_ids}
                  onChange={(e) => setPricingParams({ ...pricingParams, internet_service_ids: e.target.value as string[] })}
                  label="Internet Services"
                  renderValue={(selected) => `${selected.length} selected`}
                >
                  {retailServices && Array.isArray(retailServices) && retailServices.map((service) => (
                    <MenuItem key={service.id} value={service.id.toString()}>
                      <Checkbox checked={pricingParams.internet_service_ids.indexOf(service.id.toString()) > -1} />
                      <ListItemText primary={`${service.retail_name} - $${service.retail_monthly_price}/mo`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contract Term (months)"
                type="number"
                value={pricingParams.contract_months}
                onChange={(e) => setPricingParams({ ...pricingParams, contract_months: parseInt(e.target.value) || 0 })}
                helperText="Enter 0 for no contract"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<CalculateIcon />}
                onClick={handleCalculatePrice}
                fullWidth
              >
                Calculate Bundle Price
              </Button>
            </Grid>
          </Grid>

          {calculatedPrice && (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>Pricing Summary</Typography>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">PBX Package:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body2">${calculatedPrice.voip_price.toFixed(2)}</Typography>
                  </Grid>
                  {calculatedPrice.voip_packages && calculatedPrice.voip_packages.length > 0 && (
                    <Grid item xs={12}>
                      <Box ml={2}>
                        {calculatedPrice.voip_packages.map((pkg) => (
                          <Typography key={pkg.id} variant="caption" color="text.secondary" display="block">
                            • {pkg.name}: ${pkg.price.toFixed(2)}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Internet Services:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body2">${calculatedPrice.internet_price.toFixed(2)}</Typography>
                  </Grid>
                  {calculatedPrice.internet_services && calculatedPrice.internet_services.length > 0 && (
                    <Grid item xs={12}>
                      <Box ml={2}>
                        {calculatedPrice.internet_services.map((svc) => (
                          <Typography key={svc.id} variant="caption" color="text.secondary" display="block">
                            • {svc.name}: ${svc.price.toFixed(2)}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Add-ons:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body2">${calculatedPrice.addon_price.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body1">${calculatedPrice.subtotal.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" color="success.main">Bundle Discount:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body1" color="success.main">
                      -${calculatedPrice.discount_amount.toFixed(2)} ({calculatedPrice.discount_percentage}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6">Total Monthly:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="h6" color="primary">
                      ${calculatedPrice.total_monthly.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Monthly Savings: ${calculatedPrice.savings.toFixed(2)}
                    </Alert>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BundleManagement;